import { Ionicons } from '@expo/vector-icons';
import { useRouter } from 'expo-router';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  ActivityIndicator,
  Pressable,
  StyleSheet,
  Text,
  TextInput,
  View,
} from 'react-native';

import { Avatar } from '@/components/ui/Avatar';
import { VerifiedBadge } from '@/components/ui/VerifiedBadge';
import { fontSize, radius, spacing } from '@/constants/theme';
import type { AppColors } from '@/constants/theme';
import { useTheme } from '@/contexts/ThemeContext';
import { useAuth } from '@/contexts/AuthContext';
import { circleShortName, formatUserId } from '@/lib/format';
import { fetchProfileByUserNumber, latinizeQuery, searchProfiles, type SearchProfileResult } from '@/lib/profiles';

const GOLD = '#C9A843';

type Props = {
  /** Προαιρετικό — ενημερώνεται σε κάθε αλλαγή κειμένου, χρήσιμο αν ο γονέας
   * θέλει να κάνει κάτι με το ίδιο κείμενο αναζήτησης (π.χ. dimming κόμβων
   * στο γράφημα του Κύκλου). */
  onQueryChange?: (query: string) => void;
};

/** Μπάρα αναζήτησης χρήστη (όνομα ή HEI-ID) με animated placeholder και
 * inline dropdown αποτελεσμάτων — ίδια λογική με αυτή που είχε αρχικά η
 * αρχική οθόνη, εξαγμένη ώστε να χρησιμοποιείται και στον Κύκλο. */
export function HeiSearchBar({ onQueryChange }: Props) {
  const { t } = useTranslation();
  const { colors } = useTheme();
  const { user } = useAuth();
  const router = useRouter();
  const styles = useMemo(() => makeStyles(colors), [colors]);

  const [search, setSearch] = useState('');
  const [searchResults, setSearchResults] = useState<SearchProfileResult[]>([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const searchDebounce = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [searchFocused, setSearchFocused] = useState(false);
  const [animatedPlaceholder, setAnimatedPlaceholder] = useState('');
  const [heiLoading, setHeiLoading] = useState(false);

  useEffect(() => {
    if (searchDebounce.current) clearTimeout(searchDebounce.current);
    const q = search.trim();
    if (!q) { setSearchResults([]); return; }
    searchDebounce.current = setTimeout(() => {
      setSearchLoading(true);
      void searchProfiles(q, user?.id)
        .then((r) => setSearchResults(r.slice(0, 5)))
        .catch(() => setSearchResults([]))
        .finally(() => setSearchLoading(false));
    }, 300);
    return () => { if (searchDebounce.current) clearTimeout(searchDebounce.current); };
  }, [search, user?.id]);

  // ── Κινούμενο placeholder μπάρας αναζήτησης (typewriter effect) ──
  useEffect(() => {
    if (searchFocused || search.length > 0) { setAnimatedPlaceholder(''); return; }
    const phrases = [t('placeholder.userSearchExampleName'), t('placeholder.userSearchExampleId')];
    let phraseIndex = 0;
    let charIndex = 0;
    let deleting = false;
    let timeout: ReturnType<typeof setTimeout>;

    const tick = () => {
      const current = phrases[phraseIndex];
      if (!deleting) {
        charIndex++;
        setAnimatedPlaceholder(current.slice(0, charIndex));
        if (charIndex === current.length) {
          deleting = true;
          timeout = setTimeout(tick, 1400);
          return;
        }
        timeout = setTimeout(tick, 65);
      } else {
        charIndex--;
        setAnimatedPlaceholder(current.slice(0, charIndex));
        if (charIndex === 0) {
          deleting = false;
          phraseIndex = (phraseIndex + 1) % phrases.length;
          timeout = setTimeout(tick, 400);
          return;
        }
        timeout = setTimeout(tick, 30);
      }
    };

    timeout = setTimeout(tick, 500);
    return () => clearTimeout(timeout);
  }, [searchFocused, search.length, t]);

  async function goSearch() {
    const q = search.trim();
    if (!q) { router.push('/(app)/users'); return; }

    // Ανίχνευση UserID: HEI-00123, HEI00123, ή μόνο αριθμός, και με ελληνικά γράμματα
    const normalized = latinizeQuery(q).toUpperCase().replace(/\s/g, '');
    const heiMatch = normalized.match(/^(?:HEI-?)?(\d+)$/);
    if (heiMatch) {
      const num = parseInt(heiMatch[1], 10);
      setHeiLoading(true);
      try {
        const p = await fetchProfileByUserNumber(num);
        if (p) { router.push({ pathname: '/(app)/users/[id]', params: { id: p.id } }); return; }
      } catch {}
      finally { setHeiLoading(false); }
    }

    router.push({ pathname: '/(app)/users', params: { q } });
  }

  return (
    <View>
      <View style={styles.searchBar}>
        <Ionicons name="search" size={18} color={colors.textMuted} />
        <View style={styles.searchInputWrap}>
          <TextInput
            style={styles.searchInput}
            value={search}
            onChangeText={(v) => { setSearch(v); onQueryChange?.(v); }}
            onSubmitEditing={goSearch}
            onFocus={() => setSearchFocused(true)}
            onBlur={() => setSearchFocused(false)}
            placeholder=""
            placeholderTextColor={colors.textMuted}
            returnKeyType="search"
            autoCapitalize="words"
            autoCorrect={false}
          />
          {search.length === 0 && !searchFocused && (
            <Text style={styles.searchAnimatedPlaceholder} pointerEvents="none" numberOfLines={1}>
              {animatedPlaceholder}
              <Text style={styles.searchCaret}>▏</Text>
            </Text>
          )}
        </View>
        {search.length > 0 ? (
          <Pressable onPress={() => { setSearch(''); onQueryChange?.(''); }} hitSlop={8}>
            <Ionicons name="close-circle" size={18} color={colors.textMuted} />
          </Pressable>
        ) : (
          <Pressable onPress={goSearch} hitSlop={8}>
            <Ionicons name="arrow-forward-circle" size={22} color={GOLD} />
          </Pressable>
        )}
      </View>

      {heiLoading && (
        <View style={styles.heiLoading}>
          <Ionicons name="search" size={14} color={GOLD} />
          <Text style={styles.heiLoadingText}>{t('home.searchingUserId')}</Text>
        </View>
      )}

      {search.trim().length > 0 && !heiLoading && (
        <View style={styles.searchDropdown}>
          {searchLoading ? (
            <View style={styles.searchDropdownRow}>
              <ActivityIndicator size="small" color={GOLD} />
            </View>
          ) : searchResults.length === 0 ? (
            <View style={styles.searchDropdownRow}>
              <Text style={styles.searchDropdownEmpty}>{t('common.noResults')}</Text>
            </View>
          ) : (
            searchResults.map((p) => (
              <Pressable
                key={p.id}
                style={({ pressed }) => [styles.searchDropdownRow, pressed && styles.searchDropdownPressed]}
                onPress={() => {
                  setSearch('');
                  setSearchResults([]);
                  router.push({ pathname: '/(app)/users/[id]', params: { id: p.id } });
                }}
                accessibilityRole="button"
              >
                <View style={styles.searchResultAvatar}>
                  <Avatar uri={p.avatar_url} name={p.full_name} size={44} />
                  {p.verified && (
                    <View style={styles.searchResultVerified}>
                      <VerifiedBadge compact />
                    </View>
                  )}
                </View>
                <View style={styles.searchResultInfo}>
                  <Text style={styles.searchDropdownName} numberOfLines={1}>
                    {p.full_name?.trim() ? circleShortName(p.full_name, (p as any).nickname) : t('home.defaultUserName')}
                  </Text>
                  {p.user_number != null && (
                    <Text style={styles.searchResultId}>{formatUserId(p.user_number)}</Text>
                  )}
                </View>
              </Pressable>
            ))
          )}
        </View>
      )}
    </View>
  );
}

function makeStyles(colors: AppColors) {
  return StyleSheet.create({
    searchBar: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: spacing.sm,
      backgroundColor: colors.surfaceAlt,
      borderRadius: radius.full,
      borderWidth: 1,
      borderColor: colors.border,
      paddingHorizontal: spacing.md,
      paddingVertical: 10,
    },
    searchInputWrap: {
      flex: 1,
      justifyContent: 'center',
    },
    searchInput: {
      color: colors.text,
      fontSize: fontSize.md,
      paddingVertical: 0,
    },
    searchAnimatedPlaceholder: {
      position: 'absolute',
      left: 0,
      right: 0,
      color: colors.textMuted,
      fontSize: fontSize.md,
    },
    searchCaret: {
      color: colors.textMuted,
      opacity: 0.6,
    },
    heiLoading: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: spacing.xs,
      paddingVertical: spacing.xs,
      paddingHorizontal: spacing.sm,
    },
    heiLoadingText: {
      color: GOLD,
      fontSize: fontSize.sm,
    },
    searchDropdown: {
      marginTop: spacing.xs,
      backgroundColor: colors.surface,
      borderRadius: radius.lg,
      borderWidth: 1,
      borderColor: colors.border,
      overflow: 'hidden',
    },
    searchDropdownRow: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: spacing.md,
      paddingHorizontal: spacing.md,
      paddingVertical: spacing.sm,
      borderBottomWidth: 1,
      borderBottomColor: colors.border,
    },
    searchDropdownPressed: { backgroundColor: colors.surfaceAlt },
    searchDropdownName: {
      color: colors.text,
      fontSize: fontSize.md,
      fontWeight: '700',
    },
    searchDropdownEmpty: {
      color: colors.textMuted,
      fontSize: fontSize.sm,
    },
    searchResultAvatar: {
      position: 'relative',
    },
    searchResultVerified: {
      position: 'absolute',
      bottom: -2,
      right: -4,
    },
    searchResultInfo: {
      flex: 1,
      gap: 2,
    },
    searchResultId: {
      color: colors.textMuted,
      fontSize: 11,
      fontWeight: '600',
      letterSpacing: 0.3,
    },
  });
}
